import React, { useState, useEffect, useRef } from 'react';
import { db, auth, storage } from '../config/firebase';
import { collection, addDoc, getDocs, query, where, serverTimestamp, doc, updateDoc, deleteDoc, getDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { QRCodeSVG, QRCodeCanvas } from 'qrcode.react';
import NavBar from '../components/NavBar';

export default function ListingsPage() {
  const [listings, setListings] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingListing, setEditingListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [fullScreenQR, setFullScreenQR] = useState(null);
  const [form, setForm] = useState({
    streetAddress: '', city: '', state: '', zip: '',
    price: '', beds: '', baths: '', description: '', photos: []
  });
  const [photoUrl, setPhotoUrl] = useState('');
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const baseUrl = 'https://listlytics.web.app/property';
  const user = auth.currentUser;

  useEffect(() => {
    fetchListings();
  }, []);

  const fetchListings = async () => {
    if (!user) return;
    const q = query(collection(db, 'listings'), where('agentId', '==', user.uid));
    const snapshot = await getDocs(q);
    setListings(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    setLoading(false);
  };

  const handleZipLookup = async (zip) => {
    if (zip.length !== 5) return;
    try {
      const res = await fetch(`https://api.zippopotam.us/us/${zip}`);
      if (!res.ok) return;
      const data = await res.json();
      setForm(prev => ({
        ...prev,
        city: data.places[0]['place name'],
        state: data.places[0]['state abbreviation']
      }));
    } catch (err) {
      console.log('Zip lookup failed:', err);
    }
  };

  const getFullAddress = (f) => {
    return `${f.streetAddress}, ${f.city}, ${f.state} ${f.zip}`.trim();
  };

  const handlePhotoUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const imageCompression = (await import('browser-image-compression')).default;
      const options = { maxSizeMB: 1, maxWidthOrHeight: 1920, useWebWorker: true };
      const compressedFile = await imageCompression(file, options);
      const storageRef = ref(storage, `listings/${user.uid}/${Date.now()}_${file.name}`);
      await uploadBytes(storageRef, compressedFile);
      const url = await getDownloadURL(storageRef);
      setForm(prev => ({ ...prev, photos: [...prev.photos, url] }));
    } catch (err) {
      alert('Photo upload failed: ' + err.message);
    }
    setUploading(false);
  };

  const handleAddPhotoUrl = () => {
    if (!photoUrl.trim()) return;
    setForm(prev => ({ ...prev, photos: [...prev.photos, photoUrl.trim()] }));
    setPhotoUrl('');
  };

  const handleRemovePhoto = (index) => {
    setForm(prev => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) return;

    const agentDoc = await getDoc(doc(db, 'agents', user.uid));
    const agentData = agentDoc.exists() ? agentDoc.data() : {};
    const fullAddress = getFullAddress(form);
    const listingData = {
      ...form,
      address: fullAddress,
      agentId: user.uid,
      agentName: agentData.name || user.displayName || user.email,
      agentEmail: agentData.email || user.email,
      agentPhone: agentData.phone || '',
      agentBrokerage: agentData.brokerage || '',
    };
    if (editingListing) {
      await updateDoc(doc(db, 'listings', editingListing.id), listingData);
    } else {
      await addDoc(collection(db, 'listings'), {
        ...listingData,
        createdAt: serverTimestamp()
      });
    }
    setForm({ streetAddress: '', city: '', state: '', zip: '',
      price: '', beds: '', baths: '', description: '', photos: [] });
    setPhotoUrl('');
    setShowForm(false);
    setEditingListing(null);
    fetchListings();
  };

  const handleEdit = (listing) => {
    setEditingListing(listing);
    setForm({
      streetAddress: listing.streetAddress || listing.address || '',
      city: listing.city || '',
      state: listing.state || '',
      zip: listing.zip || '',
      price: listing.price || '',
      beds: listing.beds || '',
      baths: listing.baths || '',
      description: listing.description || '',
      photos: listing.photos || []
    });
    setShowForm(true);
    window.scrollTo(0, 0);
  };

  const handleDelete = async (listingId) => {
    if (!window.confirm('Are you sure you want to delete this listing?')) return;
    await deleteDoc(doc(db, 'listings', listingId));
    fetchListings();
  };

  const handleShare = async (listing) => {
    const url = `${baseUrl}/${listing.id}`;
    if (navigator.share) {
      await navigator.share({
        title: listing.address,
        text: `Check out this property: ${listing.address} - $${Number(listing.price).toLocaleString()}`,
        url: url
      });
    } else {
      await navigator.clipboard.writeText(url);
      alert('Link copied to clipboard!');
    }
  };

  if (loading) return <div style={styles.center}>Loading listings...</div>;

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>My Listings</h1>
        <button style={styles.addButton} onClick={() => {
          setShowForm(!showForm);
          setEditingListing(null);
          setForm({ streetAddress: '', city: '', state: '', zip: '',
            price: '', beds: '', baths: '', description: '', photos: [] });
        }}>
          {showForm ? 'Cancel' : '+ Add'}
        </button>
      </div>

      {showForm && (
        <div style={styles.form}>
          <h2 style={styles.formTitle}>{editingListing ? 'Edit Listing' : 'New Listing'}</h2>
          <form onSubmit={handleSubmit}>
            <input style={styles.input} placeholder="Street Address *"
              value={form.streetAddress}
              onChange={e => setForm({...form, streetAddress: e.target.value})} required />
            <input style={styles.input} placeholder="Zip Code"
              value={form.zip} maxLength={5}
              onChange={e => {
                setForm({...form, zip: e.target.value});
                handleZipLookup(e.target.value);
              }} />
            <div style={styles.row}>
              <input style={{...styles.input, flex: 2}} placeholder="City"
                value={form.city}
                onChange={e => setForm({...form, city: e.target.value})} />
              <input style={{...styles.input, flex: 1}} placeholder="State"
                value={form.state} maxLength={2}
                onChange={e => setForm({...form, state: e.target.value})} />
            </div>
            <input style={styles.input} placeholder="Price (e.g. 450000)"
              value={form.price} onChange={e => setForm({...form, price: e.target.value})} />
            <div style={styles.row}>
              <input style={{...styles.input, flex: 1}} placeholder="Beds"
                value={form.beds} onChange={e => setForm({...form, beds: e.target.value})} />
              <input style={{...styles.input, flex: 1}} placeholder="Baths"
                value={form.baths} onChange={e => setForm({...form, baths: e.target.value})} />
            </div>
            <textarea style={styles.textarea} placeholder="Description"
              value={form.description} onChange={e => setForm({...form, description: e.target.value})} />

            <p style={styles.photoLabel}>Photos</p>
            {form.photos.length > 0 && (
              <div style={styles.photoGrid}>
                {form.photos.map((url, index) => (
                  <div key={index} style={styles.photoThumb}>
                    <img src={url} alt={`Photo ${index + 1}`} style={styles.thumbImg} />
                    <button type="button" style={styles.removePhoto}
                      onClick={() => handleRemovePhoto(index)}>✕</button>
                  </div>
                ))}
              </div>
            )}
            <div style={styles.photoButtons}>
              <button type="button" style={styles.photoBtn}
                onClick={() => fileInputRef.current.click()}>
                📁 Upload
              </button>
              <button type="button" style={styles.photoBtn}
                onClick={() => cameraInputRef.current.click()}>
                📷 Camera
              </button>
            </div>
            <input ref={fileInputRef} type="file" accept="image/*" style={{display:'none'}}
              onChange={e => handlePhotoUpload(e.target.files[0])} />
            <input ref={cameraInputRef} type="file" accept="image/*" capture="environment"
              style={{display:'none'}} onChange={e => handlePhotoUpload(e.target.files[0])} />
            {uploading && <p style={styles.uploading}>⏳ Compressing & uploading photo...</p>}
            <div style={styles.urlRow}>
              <input style={{...styles.input, flex: 1, marginBottom: 0}}
                placeholder="Or paste photo URL"
                value={photoUrl} onChange={e => setPhotoUrl(e.target.value)} />
              <button type="button" style={styles.urlAddBtn} onClick={handleAddPhotoUrl}>Add</button>
            </div>
            <button type="submit" style={{...styles.button, marginTop: 16}} disabled={uploading}>
              {editingListing ? 'Save Changes' : 'Add Listing'}
            </button>
          </form>
        </div>
      )}

      {listings.length === 0 ? (
        <div style={styles.empty}>
          <p style={{fontSize: 48}}>🏡</p>
          <p style={{fontWeight: 'bold'}}>No listings yet.</p>
          <p style={{color: '#999'}}>Click "+ Add" to get started!</p>
        </div>
      ) : (
        listings.map(listing => (
          <div key={listing.id} style={styles.card}>
            {listing.photos?.[0] && (
              <img src={listing.photos[0]} alt="Property" style={styles.image} />
            )}
            <div style={styles.cardBody}>
              <p style={styles.cardAddress}>{listing.address}</p>
              <p style={styles.cardPrice}>${Number(listing.price).toLocaleString()}</p>
              <p style={styles.cardDetails}>
                {listing.beds} beds · {listing.baths} baths
              </p>
            </div>

            <div style={styles.qrSection}>
              <QRCodeSVG value={`${baseUrl}/${listing.id}`} size={80} />
              <div style={{marginLeft: 12, flex: 1}}>
                <p style={styles.qrLabel}>Scan to preview</p>
              </div>
            </div>

            <div style={{display: 'none'}}>
              <QRCodeCanvas
                id={`qr-canvas-${listing.id}`}
                value={`${baseUrl}/${listing.id}`}
                size={512}
              />
            </div>

            <div style={styles.qrActions}>
              <button style={styles.qrBtn} onClick={() => setFullScreenQR(listing)}>
                🔍 View QR
              </button>
              <button style={styles.qrBtn} onClick={() => handleShare(listing)}>
                🔗 Share
              </button>
            </div>

            <div style={styles.cardActions}>
              <button style={styles.editBtn} onClick={() => handleEdit(listing)}>
                ✏️ Edit
              </button>
              <button style={styles.deleteBtn} onClick={() => handleDelete(listing.id)}>
                🗑️ Delete
              </button>
            </div>
          </div>
        ))
      )}

      {fullScreenQR && (
        <div style={styles.modal} onClick={() => setFullScreenQR(null)}>
          <div style={styles.modalContent}>
            <p style={styles.modalTitle}>{fullScreenQR.address}</p>
            <QRCodeSVG
              value={`${baseUrl}/${fullScreenQR.id}`}
              size={280}
            />
            <p style={styles.modalHint}>📸 Screenshot this to save</p>
            <p style={styles.modalClose}>Tap anywhere to close</p>
          </div>
        </div>
      )}

      <NavBar />
    </div>
  );
}

const styles = {
  container: { maxWidth: 480, margin: '0 auto', padding: 20,
    fontFamily: 'sans-serif', paddingBottom: 100 },
  center: { textAlign: 'center', marginTop: 100, fontSize: 18 },
  header: { display: 'flex', justifyContent: 'space-between',
    alignItems: 'center', marginBottom: 20 },
  title: { fontSize: 24, fontWeight: 'bold', margin: 0 },
  addButton: { background: '#2E86AB', color: '#fff', border: 'none',
    borderRadius: 8, padding: '10px 16px', fontSize: 14,
    fontWeight: 'bold', cursor: 'pointer' },
  form: { background: '#f9f9f9', borderRadius: 12, padding: 20, marginBottom: 20 },
  formTitle: { fontSize: 18, fontWeight: 'bold', marginBottom: 12 },
  row: { display: 'flex', gap: 10 },
  input: { width: '100%', padding: 12, marginBottom: 10, fontSize: 15,
    border: '1px solid #ddd', borderRadius: 8, boxSizing: 'border-box' },
  textarea: { width: '100%', padding: 12, marginBottom: 10, fontSize: 15,
    border: '1px solid #ddd', borderRadius: 8, height: 80, boxSizing: 'border-box' },
  photoLabel: { fontSize: 15, fontWeight: 'bold', margin: '8px 0' },
  photoGrid: { display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 },
  photoThumb: { position: 'relative', width: 80, height: 80 },
  thumbImg: { width: 80, height: 80, objectFit: 'cover', borderRadius: 8 },
  removePhoto: { position: 'absolute', top: -6, right: -6, background: '#e74c3c',
    color: '#fff', border: 'none', borderRadius: 10, width: 20, height: 20,
    fontSize: 10, cursor: 'pointer' },
  photoButtons: { display: 'flex', gap: 10, marginBottom: 10 },
  photoBtn: { flex: 1, padding: 10, background: '#fff', border: '1px solid #ddd',
    borderRadius: 8, fontSize: 14, cursor: 'pointer' },
  uploading: { color: '#2E86AB', fontSize: 14, margin: '4px 0' },
  urlRow: { display: 'flex', gap: 8, alignItems: 'center' },
  urlAddBtn: { padding: '12px 16px', background: '#2E86AB', color: '#fff',
    border: 'none', borderRadius: 8, fontSize: 14, cursor: 'pointer' },
  button: { width: '100%', padding: 14, background: '#2E86AB', color: '#fff',
    border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 'bold', cursor: 'pointer' },
  empty: { textAlign: 'center', color: '#555', marginTop: 60 },
  card: { border: '1px solid #eee', borderRadius: 12, marginBottom: 16, overflow: 'hidden' },
  image: { width: '100%', height: 160, objectFit: 'cover' },
  cardBody: { padding: '12px 16px' },
  cardAddress: { fontSize: 15, fontWeight: 'bold', margin: '0 0 4px' },
  cardPrice: { fontSize: 16, color: '#2E86AB', fontWeight: 'bold', margin: '0 0 4px' },
  cardDetails: { fontSize: 13, color: '#666', margin: 0 },
  qrSection: { padding: '12px 16px', borderTop: '1px solid #eee',
    display: 'flex', alignItems: 'center' },
  qrLabel: { fontSize: 13, fontWeight: 'bold', margin: '0 0 2px' },
  qrActions: { display: 'flex', gap: 8, padding: '0 16px 8px' },
  qrBtn: { flex: 1, padding: 8, background: '#f0f8ff', color: '#2E86AB',
    border: '1px solid #2E86AB', borderRadius: 8, fontSize: 12, cursor: 'pointer' },
  cardActions: { display: 'flex', gap: 8, padding: '8px 16px 12px',
    borderTop: '1px solid #eee' },
  editBtn: { flex: 1, padding: 10, background: '#f0f8ff', color: '#2E86AB',
    border: '1px solid #2E86AB', borderRadius: 8, fontSize: 14, cursor: 'pointer' },
  deleteBtn: { flex: 1, padding: 10, background: '#fff5f5', color: '#e74c3c',
    border: '1px solid #e74c3c', borderRadius: 8, fontSize: 14, cursor: 'pointer' },
  modal: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.9)', display: 'flex', alignItems: 'center',
    justifyContent: 'center', zIndex: 1000 },
  modalContent: { background: '#fff', borderRadius: 16, padding: 32,
    textAlign: 'center', margin: 20 },
  modalTitle: { fontSize: 16, fontWeight: 'bold', marginBottom: 20, color: '#333' },
  modalHint: { fontSize: 14, color: '#2E86AB', fontWeight: 'bold', margin: '16px 0 8px' },
  modalClose: { fontSize: 12, color: '#999', margin: 0 }
};

